import { c as _c } from "react/compiler-runtime";
import { NULL_ADDRESS } from "@opensea/utils";
import { useCallback, useState } from "react";
import { useConfig, useDeployContract, useSendTransaction } from "wagmi";
import { estimateGas, waitForTransactionReceipt } from "wagmi/actions";
import { useAddress } from "../../hooks/useAddress";
import { useRequireChain } from "../../hooks/useRequireChain";
import { parseTransactionError, TransactionRevertedError } from "../../lib/errors";
import { useWithConnectorErrorRetry } from "./useWithConnectorErrorRetry";
export const useEvmTransactFunction = () => {
  const $ = _c(16);
  const config = useConfig();
  const {
    requireChain
  } = useRequireChain();
  const address = useAddress();
  const {
    sendTransactionAsync,
    isPending: isPendingTx,
    isIdle: isIdleTx,
    isPaused: isPausedTx,
    isSuccess: isSuccessTx,
    isError: isErrorTx,
    data: dataTx
  } = useSendTransaction();
  const {
    deployContractAsync,
    isPending: isPendingDeploy,
    isIdle: isIdleDeploy,
    isPaused: isPausedDeploy,
    isSuccess: isSuccessDeploy,
    isError: isErrorDeploy,
    data: dataDeploy
  } = useDeployContract();
  const [error, setError] = useState();
  const withConnectorErrorRetry = useWithConnectorErrorRetry();
  let t0;
  if ($[0] !== address || $[1] !== config || $[2] !== deployContractAsync || $[3] !== requireChain || $[4] !== sendTransactionAsync || $[5] !== withConnectorErrorRetry) {
    t0 = async (variables, t1) => {
      const {
        shouldBufferGas: t2
      } = t1 === undefined ? {} : t1;
      const shouldBufferGas = t2 === undefined ? false : t2;
      const {
        chainId
      } = variables;
      if (!chainId) {
        throw new Error("No chain specified");
      }
      ;
      try {
        const hash = await withConnectorErrorRetry(async () => {
          await requireChain(chainId);
          let gas = variables.gas;
          if (shouldBufferGas) {
            if (gas === undefined) {
              gas = await estimateGas(config, {
                ...variables,
                account: variables.address ?? address,
                chainId
              });
            }
            gas = bufferGas(gas);
          }
          if (variables.to === NULL_ADDRESS) {
            return await deployContractAsync({
              chainId: variables.chainId,
              abi: [],
              bytecode: variables.data,
              gas,
              account: variables.address ?? address
            });
          }
          return await sendTransactionAsync({
            ...variables,
            gas,
            account: variables.address ?? address
          });
        });
        setError(undefined);
        const wait = async opts => {
          const {
            confirmations: t4,
            pollingInterval,
            timeout
          } = opts || {};
          const confirmations = t4 === undefined ? 1 : t4;
          ;
          try {
            const receipt = await waitForTransactionReceipt(config, {
              hash: hash,
              confirmations,
              pollingInterval,
              timeout,
              chainId
            });
            if (receipt.status === "success") {
              return {
                status: "success",
                hash
              };
            }
            return {
              status: "failed",
              hash,
              error: new TransactionRevertedError(Error("Transaction Reverted"))
            };
          } catch (t5) {
            const error_1 = t5;
            return {
              status: "failed",
              hash,
              error: parseTransactionError(error_1)
            };
          }
        };
        return {
          hash,
          wait
        };
      } catch (t3) {
        const error_0 = t3;
        const externalError = parseTransactionError(error_0);
        setError(externalError);
        throw externalError;
      }
    };
    $[0] = address;
    $[1] = config;
    $[2] = deployContractAsync;
    $[3] = requireChain;
    $[4] = sendTransactionAsync;
    $[5] = withConnectorErrorRetry;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const transact = t0;
  const isPending = isPendingTx || isPendingDeploy;
  const isSuccess = isSuccessTx || isSuccessDeploy;
  const isError = isErrorTx || isErrorDeploy;
  const isIdle = isIdleTx && isIdleDeploy;
  const isPaused = isPausedTx || isPausedDeploy;
  const data = dataTx ?? dataDeploy;
  let t1;
  if ($[7] !== data || $[8] !== error || $[9] !== isError || $[10] !== isIdle || $[11] !== isPaused || $[12] !== isPending || $[13] !== isSuccess || $[14] !== transact) {
    t1 = {
      transact,
      isPending,
      isSuccess,
      isError,
      isIdle,
      isPaused,
      data,
      error
    };
    $[7] = data;
    $[8] = error;
    $[9] = isError;
    $[10] = isIdle;
    $[11] = isPaused;
    $[12] = isPending;
    $[13] = isSuccess;
    $[14] = transact;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  return t1;
};

// Add a 10% buffer to the gas limit
const bufferGas = gas => {
  const multiplier = BigInt(115);
  const divisor = BigInt(100);
  return gas * multiplier / divisor;
};